import React, { useEffect, useRef, useState } from "react";
import {
  Dashboard as DashboardIcon,
  BarChart as BarChartIcon,
  ShowChart as LineChartIcon,
  PieChart as PieChartIcon,
  Timeline as AreaChartIcon,
  Settings as SettingsIcon,
  Tune as TuneIcon,
  Search as SearchIcon,
  CloseRounded as CloseRoundedIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Select,
  Skeleton,
  Grid,
  TextField,
  Tabs,
  Tab,
  IconButton,
  alpha,
} from "@mui/material";
import { useDashboardStore } from "../stores/dashboard.store";
import { useOrganizationStore } from "../stores/organization.store";
import { DashboardStatistics, StatisticType } from "../types/dashboard.type";
import {
  OrganizationType,
  isSiteOperationsOrgType,
} from "../types/organization.type";
import { useTranslation } from "react-i18next";
import PageTitleIcon from "../components/PageTitleIcon";
import { SetupCoachmark } from "../components/selfSetup/SetupCoachmark";
import { useSelfSetupTour } from "../hooks/useSelfSetupTour";
import { HELP_GUIDE_CUSTOMIZE, HELP_GUIDE_KPI_LIMITS } from "../constants/knowledgeBaseTryIt";
import { APP_FONT_FAMILY } from "../constants/fonts";
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from "../utils/helpGuideSession";
import { helpI18n } from "../i18n/help";
import StatCard from "../components/dashboard/StatCard";
import ChartCard from "../components/dashboard/ChartCard";
import TruckMap from "../components/dashboard/TruckMap";
import ContainerMap from "../components/dashboard/ContainerMap";
import KPILimitsDialog from "../components/dashboard/KPILimitsDialog";
import KPIProgressCard from "../components/dashboard/KPIProgressCard";
import { format } from "date-fns";
import { useNavigate, useLocation } from "react-router-dom";
import { useKPIStore } from "../stores/kpi.store";
import {
  getStatsForOrgType,
  getDefaultStatsForOrgType,
  isStatAllowedForOrgType,
  getStatDisplayTitle,
  getDashboardExtrasForOrgTab,
  DASHBOARD_EXTRA_KPI,
  DASHBOARD_EXTRA_MAP,
  type DashboardExtraWidgetId,
} from "../constants/dashboardStats.catalog";
import DashboardCustomizeWidgetList from "../components/dashboard/DashboardCustomizeWidgetList";
import {
  sanitizePickableIds,
  ZERO_MONTHLY_EXTRAS_KEY,
  ZERO_OVERVIEW_EXTRAS_KEY,
  ZERO_OVERVIEW_STATS_KEY,
} from "../utils/dashboardCustomizePreferences";
import { useAuthStore } from "../stores/auth.store";
import PilotDashboard from "./PilotDashboard";

const StandardDashboard: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { t: tHelp } = useTranslation("translation", { i18n: helpI18n });
  const { selectedOrganization } = useOrganizationStore();
  const {
    statistics,
    aggregatedStatistics,
    selectedStats,
    isLoading,
    isLoadingAggregated,
    error,
    aggregatedError,
    selectedMonth,
    fetchDashboardStatistics,
    fetchAggregatedDashboardStatistics,
    clearAggregatedDashboard,
    setSelectedStats,
    setSelectedMonth,
    loadDashboardPreferences,
  } = useDashboardStore();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isStep,
    completeStep,
    dismissTour,
    tourPhase,
    setTourPhase,
  } = useSelfSetupTour();
  const showDashboardTour = isStep("dashboard");
  const [helpCustomizeGuide, setHelpCustomizeGuide] = useState(false);
  const [helpKpiGuide, setHelpKpiGuide] = useState(false);
  const [kpiTourPhase, setKpiTourPhase] = useState<"button" | "form">("button");
  /** Knowledge Base customize guide local phase (self-setup uses tourPhase). */
  const [helpCustomizePhase, setHelpCustomizePhase] = useState<
    "button" | "dialog"
  >("button");
  type CustomizeDialogPhase = "search" | "metrics" | "save";
  const [customizeDialogPhase, setCustomizeDialogPhase] =
    useState<CustomizeDialogPhase>("search");

  const { limits } = useKPIStore();
  const hasEnabledKPIs = limits.some((limit) => limit.isEnabled);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedStat, setSelectedStat] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openKPIDialog, setOpenKPIDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [chartTypes, setChartTypes] = useState<
    Record<string, "bar" | "line" | "area" | "pie">
  >({});
  const [dashboardViewTab, setDashboardViewTab] = useState(0);
  const [selectedOverviewStats, setSelectedOverviewStats] = useState<
    StatisticType[]
  >([]);
  const [monthlyExtras, setMonthlyExtras] = useState<DashboardExtraWidgetId[]>(
    [],
  );
  const [overviewExtras, setOverviewExtras] = useState<DashboardExtraWidgetId[]>(
    [],
  );

  /** Self-setup on-page tip or Knowledge Base customize guide. */
  const activeCustomizeGuide =
    (showDashboardTour &&
      (tourPhase === "customizeButton" ||
        tourPhase === "dialogSearch" ||
        tourPhase === "dialogMetrics" ||
        tourPhase === "dialogSave")) ||
    helpCustomizeGuide;

  const showCustomizeButtonTip =
    (showDashboardTour && tourPhase === "customizeButton") ||
    (helpCustomizeGuide && helpCustomizePhase === "button");

  const selfSetupDialogPhase: CustomizeDialogPhase | null =
    tourPhase === "dialogSearch"
      ? "search"
      : tourPhase === "dialogMetrics"
        ? "metrics"
        : tourPhase === "dialogSave"
          ? "save"
          : null;

  const showCustomizeDialogTip =
    (showDashboardTour && selfSetupDialogPhase != null) ||
    (helpCustomizeGuide && helpCustomizePhase === "dialog");

  const activeCustomizeDialogPhase: CustomizeDialogPhase =
    selfSetupDialogPhase ?? customizeDialogPhase;

  const finishKpiGuide = () => {
    setHelpKpiGuide(false);
    setKpiTourPhase("button");
    setOpenKPIDialog(false);
    clearHelpGuideActive();
  };

  /** Close tip without completing the dashboard step (Later / X). */
  const softDismissDashboardTour = () => {
    setOpenDialog(false);
    setOpenKPIDialog(false);
    if (helpCustomizeGuide) {
      setHelpCustomizeGuide(false);
      setHelpCustomizePhase("button");
      setCustomizeDialogPhase("search");
      clearHelpGuideActive();
      return;
    }
    // Keep current tourPhase so dismissTour saves the exact tip (incl. dialog*).
    dismissTour();
  };

  const advanceCustomizeDialogTour = () => {
    if (showDashboardTour && selfSetupDialogPhase) {
      if (tourPhase === "dialogSearch") {
        setTourPhase("dialogMetrics");
        return;
      }
      if (tourPhase === "dialogMetrics") {
        setTourPhase("dialogSave");
        return;
      }
      leaveCustomizeToKeyNumbers();
      return;
    }
    if (customizeDialogPhase === "search") {
      setCustomizeDialogPhase("metrics");
      return;
    }
    if (customizeDialogPhase === "metrics") {
      setCustomizeDialogPhase("save");
      return;
    }
    leaveCustomizeToKeyNumbers();
  };

  /** After customize tips: continue to Key Numbers (do not complete dashboard yet). */
  const leaveCustomizeToKeyNumbers = () => {
    setOpenDialog(false);
    setCustomizeDialogPhase("search");
    setHelpCustomizePhase("button");
    if (helpCustomizeGuide) {
      setHelpCustomizeGuide(false);
      clearHelpGuideActive();
      return;
    }
    setTourPhase("keyNumbers");
  };

  const goNextDashboardTip = () => {
    if (!showDashboardTour) return;
    if (tourPhase === "monthlyTab") {
      setTourPhase("headerActions");
      return;
    }
    if (tourPhase === "headerActions") {
      setOpenKPIDialog(true);
      setTourPhase("kpiForm");
      return;
    }
    if (tourPhase === "kpiForm") {
      setTourPhase("kpiList");
      return;
    }
    if (tourPhase === "kpiList") {
      setOpenKPIDialog(false);
      setDashboardViewTab(0);
      setTourPhase("monthSelect");
      return;
    }
    if (tourPhase === "monthSelect") {
      setTourPhase("customizeButton");
      return;
    }
    if (tourPhase === "keyNumbers") {
      setTourPhase("charts");
      return;
    }
    if (tourPhase === "charts") {
      setOpenDialog(false);
      setOpenKPIDialog(false);
      completeStep("dashboard");
    }
  };

  // Keep MONTHLY tab selected while guiding month-related tips.
  useEffect(() => {
    if (
      !showDashboardTour ||
      (tourPhase !== "monthlyTab" &&
        tourPhase !== "headerActions" &&
        tourPhase !== "kpiForm" &&
        tourPhase !== "kpiList" &&
        tourPhase !== "monthSelect")
    ) {
      return;
    }
    setDashboardViewTab(0);
  }, [showDashboardTour, tourPhase]);

  // Keep KPI / Customize dialogs open while their self-setup tips are active (incl. resume).
  useEffect(() => {
    if (!showDashboardTour) return;
    if (tourPhase === "kpiForm" || tourPhase === "kpiList") {
      setOpenKPIDialog(true);
    }
    if (
      tourPhase === "dialogSearch" ||
      tourPhase === "dialogMetrics" ||
      tourPhase === "dialogSave"
    ) {
      setOpenDialog(true);
    }
  }, [showDashboardTour, tourPhase]);

  // Only reset help-customize local phase when that guide ends.
  const wasHelpCustomizeRef = useRef(false);
  useEffect(() => {
    if (!helpCustomizeGuide) {
      if (wasHelpCustomizeRef.current) {
        setHelpCustomizePhase("button");
        setCustomizeDialogPhase("search");
      }
      wasHelpCustomizeRef.current = false;
      return;
    }
    if (!wasHelpCustomizeRef.current) {
      setHelpCustomizePhase("button");
    }
    wasHelpCustomizeRef.current = true;
  }, [helpCustomizeGuide]);

  useEffect(() => {
    if (!showCustomizeButtonTip) return;
    window.requestAnimationFrame(() => {
      document
        .getElementById("self-setup-dashboard-customize")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [showCustomizeButtonTip]);

  useEffect(() => {
    if (!showCustomizeDialogTip || !openDialog) return;
    const phase = activeCustomizeDialogPhase;
    const id =
      phase === "search"
        ? "help-customize-search"
        : phase === "metrics"
          ? "help-customize-metrics"
          : "help-customize-save";
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, [showCustomizeDialogTip, activeCustomizeDialogPhase, openDialog]);

  useEffect(() => {
    if (!showDashboardTour) return;
    const idByPhase: Partial<Record<string, string>> = {
      monthlyTab: "self-setup-dashboard-monthly-tab",
      headerActions: "help-kpi-limits-button",
      kpiForm: "self-setup-kpi-form",
      kpiList: "self-setup-kpi-list",
      monthSelect: "self-setup-dashboard-month-select",
      keyNumbers: "self-setup-dashboard-key-numbers",
      charts: "self-setup-dashboard-charts",
      customizeButton: "self-setup-dashboard-customize",
    };
    const id = idByPhase[tourPhase];
    if (!id) return;
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, [showDashboardTour, tourPhase]);

  // Skip empty Key Numbers / Charts sections during the guided tour.
  useEffect(() => {
    if (!showDashboardTour) return;
    if (tourPhase !== "keyNumbers" && tourPhase !== "charts") return;
    if (dashboardViewTab === 0 && isLoading) return;
    if (dashboardViewTab === 1 && isLoadingAggregated) return;

    const frame = window.requestAnimationFrame(() => {
      if (tourPhase === "keyNumbers") {
        if (!document.getElementById("self-setup-dashboard-key-numbers")) {
          if (document.getElementById("self-setup-dashboard-charts")) {
            setTourPhase("charts");
          } else {
            completeStep("dashboard");
          }
        }
        return;
      }
      if (
        tourPhase === "charts" &&
        !document.getElementById("self-setup-dashboard-charts")
      ) {
        completeStep("dashboard");
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    showDashboardTour,
    tourPhase,
    dashboardViewTab,
    isLoading,
    isLoadingAggregated,
    statistics,
    aggregatedStatistics,
    selectedStats,
    setTourPhase,
    completeStep,
  ]);

  useEffect(() => {
    if (!helpKpiGuide || kpiTourPhase !== "button") return;
    window.requestAnimationFrame(() => {
      document
        .getElementById("help-kpi-limits-button")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [helpKpiGuide, kpiTourPhase]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const setup = params.get("selfSetup");
    const helpGuide = params.get("helpGuide");
    if (!setup && !helpGuide) return;

    if (setup === "kpi") {
      setOpenKPIDialog(true);
    }
    if (helpGuide === HELP_GUIDE_KPI_LIMITS) {
      markHelpGuideActive(helpGuide);
      setHelpKpiGuide(true);
      setKpiTourPhase("button");
    }
    const isCustomize =
      setup === "customize" || helpGuide === HELP_GUIDE_CUSTOMIZE;
    if (isCustomize) {
      if (helpGuide === HELP_GUIDE_CUSTOMIZE) {
        markHelpGuideActive(helpGuide);
        setHelpCustomizeGuide(true);
        setHelpCustomizePhase("button");
      }
    }

    // Only strip query when Dashboard consumed it — leave language/appearance for Layout.
    if (
      setup ||
      helpGuide === HELP_GUIDE_CUSTOMIZE ||
      helpGuide === HELP_GUIDE_KPI_LIMITS
    ) {
      navigate({ pathname: location.pathname, search: "" }, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
    if (showDashboardTour && tourPhase === "customizeButton") {
      setTourPhase("dialogSearch");
    } else if (helpCustomizeGuide) {
      setHelpCustomizePhase("dialog");
      setCustomizeDialogPhase("search");
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    if (
      showDashboardTour &&
      (tourPhase === "dialogSearch" ||
        tourPhase === "dialogMetrics" ||
        tourPhase === "dialogSave")
    ) {
      setTourPhase("customizeButton");
    } else if (helpCustomizeGuide && helpCustomizePhase === "dialog") {
      setHelpCustomizePhase("button");
      setCustomizeDialogPhase("search");
    }
  };

  const getDisplayTitle = (statKey: string): string => {
    return getStatDisplayTitle(
      statKey,
      selectedOrganization?.organization_type
    );
  };

  const [availableMonths] = useState(() => {
    const months = [];
    const currentDate = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1
      );
      months.push({
        value: format(date, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        label: format(date, "MMMM yyyy"),
      });
    }
    return months;
  });

  useEffect(() => {
    loadDashboardPreferences();

    const orgType = selectedOrganization?.organization_type;
    if (!orgType) return;

    const { selectedMonth: storedMonth, selectedStats: storedStats } =
      useDashboardStore.getState();

    const currentDate = new Date();
    const defaultMonth = format(
      new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
      "yyyy-MM-dd'T'HH:mm:ss'Z'"
    );

    const monthToUse = storedMonth || defaultMonth;
    if (monthToUse !== storedMonth) {
      setSelectedMonth(monthToUse);
    }

    // Keep only stats relevant to this org type; otherwise fall back to type defaults.
    const allowedStored = (storedStats ?? []).filter((stat) =>
      isStatAllowedForOrgType(stat, orgType)
    );
    const monthlyStats =
      allowedStored.length > 0
        ? allowedStored
        : getDefaultStatsForOrgType(orgType);
    setSelectedStats(monthlyStats);

    const monthlyExtraIds = getDashboardExtrasForOrgTab(orgType, "monthly");
    const overviewExtraIds = getDashboardExtrasForOrgTab(orgType, "overview");
    try {
      const storedMonthlyExtras = sessionStorage.getItem(ZERO_MONTHLY_EXTRAS_KEY);
      setMonthlyExtras(
        sanitizePickableIds(
          storedMonthlyExtras ? JSON.parse(storedMonthlyExtras) : undefined,
          monthlyExtraIds,
        ),
      );
    } catch {
      setMonthlyExtras([...monthlyExtraIds]);
    }
    try {
      const storedOverviewExtras = sessionStorage.getItem(ZERO_OVERVIEW_EXTRAS_KEY);
      setOverviewExtras(
        sanitizePickableIds(
          storedOverviewExtras ? JSON.parse(storedOverviewExtras) : undefined,
          overviewExtraIds,
        ),
      );
    } catch {
      setOverviewExtras([...overviewExtraIds]);
    }

    const allowedOverview = (ids: StatisticType[]) =>
      ids.filter((stat) => isStatAllowedForOrgType(stat, orgType));
    try {
      const storedOverview = sessionStorage.getItem(ZERO_OVERVIEW_STATS_KEY);
      if (storedOverview) {
        const parsed = JSON.parse(storedOverview) as StatisticType[];
        const next = allowedOverview(parsed);
        setSelectedOverviewStats(
          parsed.length === 0
            ? []
            : next.length > 0
              ? next
              : monthlyStats,
        );
      } else {
        setSelectedOverviewStats(monthlyStats);
        sessionStorage.setItem(
          ZERO_OVERVIEW_STATS_KEY,
          JSON.stringify(monthlyStats),
        );
      }
    } catch {
      setSelectedOverviewStats(monthlyStats);
    }

    if (selectedOrganization?.id) {
      fetchDashboardStatistics(selectedOrganization.id, orgType, monthToUse);
    }
  }, [selectedOrganization?.id, selectedOrganization?.organization_type]);

  useEffect(() => {
    clearAggregatedDashboard();
  }, [selectedOrganization?.id, clearAggregatedDashboard]);

  useEffect(() => {
    if (
      dashboardViewTab !== 1 ||
      !selectedOrganization?.id ||
      !selectedOrganization?.organization_type
    ) {
      return;
    }
    fetchAggregatedDashboardStatistics(
      selectedOrganization.id,
      selectedOrganization.organization_type
    );
  }, [
    dashboardViewTab,
    selectedOrganization?.id,
    selectedOrganization?.organization_type,
    fetchAggregatedDashboardStatistics,
  ]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    statId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedStat(statId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedStat(null);
  };

  const handleChartTypeChange = (type: "bar" | "line" | "area" | "pie") => {
    if (selectedStat) {
      setChartTypes((prev) => ({ ...prev, [selectedStat]: type }));
      handleMenuClose();
    }
  };

  const handleCloseKPIDialog = () => {
    if (helpKpiGuide) {
      finishKpiGuide();
      return;
    }
    setOpenKPIDialog(false);
    // Same as Customize/Compare: closing the dialog returns to the trigger
    // tip — do NOT Later-dismiss the whole onboarding (that hid the flow
    // after logout/re-login when users closed KPI to reach the account menu).
    if (
      showDashboardTour &&
      (tourPhase === "kpiForm" || tourPhase === "kpiList")
    ) {
      setTourPhase("headerActions");
      return;
    }
    clearHelpGuideActive();
  };

  const handleSavePreferences = () => {
    setOpenDialog(false);
    if (activeCustomizeGuide) {
      leaveCustomizeToKeyNumbers();
    }
  };

  const handleMonthChange = (date: string) => {
    setSelectedMonth(date);
    if (selectedOrganization?.id && selectedOrganization?.organization_type) {
      fetchDashboardStatistics(
        selectedOrganization.id,
        selectedOrganization.organization_type,
        date
      );
    }
    if (showDashboardTour && tourPhase === "monthSelect") {
      goNextDashboardTip();
    }
  };

  const KPI_UNITS: Record<string, string> = {
    // orders
    total_orders: "orders",
    pending_orders: "orders",
    completed_orders: "orders",
    cancelled_orders: "orders",

    // revenue & incentives
    total_revenue: "SAR",
    average_incentive_per_kg: "SAR/kg",

    // percentages
    full_percentage: "%",
    completion_rate: "%",
    cancellation_rate: "%",
    waste_diversion_rate: "%",
    quality_compliance_rate: "%",
    rejected_waste_percentage: "%",
    storage_capacity_utilization: "%",
    processing_capacity_utilization: "%",

    // weights
    total_waste_amount: "kg",
    completed_waste_amount: "kg",
    sustainable_supply: "kg",

    // time
    average_processing_time_hours: "hours",
  };

  const getUnitForStat = (stat: StatisticType): string => {
    switch (stat) {
      case StatisticType.TIME_FROM_FULL_TO_COLLECTION:
      case StatisticType.TIME_FROM_COLLECTION_TO_DELIVERY:
      case StatisticType.AVERAGE_PROCESSING_TIME:
        return "hours";
      case StatisticType.FULL_PERCENTAGE:
      case StatisticType.WASTE_DIVERSION_RATE:
      case StatisticType.QUALITY_COMPLIANCE_RATE:
      case StatisticType.REJECTED_WASTE_PERCENTAGE:
      case StatisticType.PROCESSING_CAPACITY_UTILIZATION:
      case StatisticType.MAINTENANCE_SCHEDULE_COMPLIANCE:
      case StatisticType.DRIVER_PERFORMANCE_SCORE:
      case StatisticType.CUSTOMER_SATISFACTION_SCORE:
      case StatisticType.WASTE_COMPACTION_EFFICIENCY:
      case StatisticType.STORAGE_CAPACITY_UTILIZATION:
      case StatisticType.WASTE_QUALITY_RATING:
      case StatisticType.AVERAGE_CONTAINER_FILL_RATE:
      case StatisticType.COMPLETION_RATE:
      case StatisticType.CANCELLATION_RATE:
        return "%";
      case StatisticType.RECYCLER_APPETITE:
      case StatisticType.SUSTAINABLE_SUPPLY:
      case StatisticType.FUEL_CONSUMPTION_PER_TRIP:
        return "kg/day";
      case StatisticType.AVERAGE_INCENTIVE_PER_KG:
        return "SAR/kg";
      // case StatisticType.TOTAL_REVENUE_LAST_MONTH:
      case StatisticType.TOTAL_REVENUE:
        return "SAR";
      case StatisticType.COLLECTION_FREQUENCY:
        return "times/day";
      case StatisticType.AVERAGE_ROUTE_EFFICIENCY:
        return "km/L";
      case StatisticType.TRIPS_TODAY_COUNT:
      case StatisticType.EMERGENCY_COLLECTION_REQUESTS:
        return "trips";
      case StatisticType.TOTAL_ORDERS:
        return "orders";
      case StatisticType.TRUCK_DRIVER_COUNT:
        return "drivers";
      default:
        return "";
    }
  };

  if (!selectedOrganization?.id) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <Typography>{t("dashboard.selectOrganization")}</Typography>
      </Box>
    );
  }

  const displayStatistics =
    dashboardViewTab === 0 ? statistics : aggregatedStatistics;

  const renderDashboardHeader = () => (
    <Paper
      elevation={2}
      sx={{
        mb: 3,
        p: 3,
        borderRadius: "16px",
        bgcolor: theme.palette.background.paper,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <PageTitleIcon icon={<DashboardIcon />} />
          <Typography variant="h4" sx={{ fontWeight: 600, color: "text.primary" }}>
            {t("dashboard.title")}
          </Typography>
        </Box>
        <Box
          sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}
        >
          <SetupCoachmark
            open={
              (showDashboardTour && tourPhase === "headerActions") ||
              (helpKpiGuide && kpiTourPhase === "button")
            }
            title={
              showDashboardTour && tourPhase === "headerActions"
                ? t("selfSetup.tour.headerActionsTitle")
                : tHelp("knowledgeBase.kpiGuideButtonTitle")
            }
            subtitle={
              showDashboardTour && tourPhase === "headerActions"
                ? t("selfSetup.tour.headerActionsSubtitle")
                : tHelp("knowledgeBase.kpiGuideButtonSubtitle")
            }
            onDismiss={
              showDashboardTour && tourPhase === "headerActions"
                ? softDismissDashboardTour
                : finishKpiGuide
            }
            onNext={
              showDashboardTour && tourPhase === "headerActions"
                ? goNextDashboardTip
                : undefined
            }
            nextLabel={t("selfSetup.tourNext")}
          >
            <Button
              id="help-kpi-limits-button"
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => {
                setOpenKPIDialog(true);
                if (helpKpiGuide) setKpiTourPhase("form");
                if (showDashboardTour && tourPhase === "headerActions") {
                  setTourPhase("kpiForm");
                }
              }}
              sx={{ borderRadius: "12px", px: 3, py: 1.5 }}
            >
              {t("dashboard.kpiLimits")}
            </Button>
          </SetupCoachmark>
          <SetupCoachmark
            open={showCustomizeButtonTip}
            title={
              helpCustomizeGuide
                ? tHelp("knowledgeBase.customizeGuideTitle")
                : t("selfSetup.tour.dashboardTitle")
            }
            subtitle={
              helpCustomizeGuide
                ? tHelp("knowledgeBase.customizeGuideSubtitle")
                : t("selfSetup.tour.dashboardSubtitle")
            }
            onDismiss={softDismissDashboardTour}
          >
            <Button
              id="self-setup-dashboard-customize"
              variant="outlined"
              startIcon={<TuneIcon />}
              onClick={() => {
                handleOpenDialog();
              }}
              aria-label={t("dashboard.customize")}
              sx={{
                borderRadius: "12px",
                px: 2.25,
                py: 1,
                minHeight: 40,
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
                gap: 0.75,
              }}
            >
              {t("dashboard.customize")}
            </Button>
          </SetupCoachmark>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          borderTop: 1,
          borderColor: "divider",
          pt: 2,
        }}
      >
        <SetupCoachmark
          open={showDashboardTour && tourPhase === "monthlyTab"}
          title={t("selfSetup.tour.monthlyTabTitle")}
          subtitle={t("selfSetup.tour.monthlyTabSubtitle")}
          onDismiss={softDismissDashboardTour}
          onNext={goNextDashboardTip}
          nextLabel={t("selfSetup.tourNext")}
        >
          <Tabs
            id="self-setup-dashboard-monthly-tab"
            value={dashboardViewTab}
            onChange={(_, v) => setDashboardViewTab(v)}
            sx={{ minHeight: 0, "& .MuiTab-root": { minHeight: 40 } }}
          >
            <Tab label={t("dashboard.tabs.monthly")} />
            <Tab label={t("dashboard.tabs.organizationOverview")} />
          </Tabs>
        </SetupCoachmark>
        {dashboardViewTab === 0 ? (
          <SetupCoachmark
            open={showDashboardTour && tourPhase === "monthSelect"}
            title={t("selfSetup.tour.monthSelectTitle")}
            subtitle={t("selfSetup.tour.monthSelectSubtitle")}
            onDismiss={softDismissDashboardTour}
            onNext={goNextDashboardTip}
            nextLabel={t("selfSetup.tourNext")}
          >
            <Select
              id="self-setup-dashboard-month-select"
              size="small"
              value={selectedMonth}
              onChange={(e) => handleMonthChange(e.target.value)}
              sx={{ minWidth: "200px", borderRadius: "12px" }}
            >
              {availableMonths.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </SetupCoachmark>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Lifetime performance metrics.
          </Typography>
        )}
      </Box>
    </Paper>
  );

  const renderSectionHeader = (title: string) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
      <Box
        sx={{
          width: 4,
          height: 22,
          borderRadius: 2,
          bgcolor: theme.palette.primary.main,
        }}
      />
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
    </Box>
  );

  const renderDashboardSkeleton = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Grid item xs={12} sm={6} md={4} key={`stat-skel-${i}`}>
            <Skeleton
              variant="rounded"
              height={140}
              sx={{ borderRadius: "16px" }}
            />
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={3}>
        {Array.from({ length: 2 }).map((_, i) => (
          <Grid item xs={12} md={6} key={`chart-skel-${i}`}>
            <Skeleton
              variant="rounded"
              height={320}
              sx={{ borderRadius: "16px" }}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  if (dashboardViewTab === 0 && isLoading) {
    if (!showDashboardTour && !helpCustomizeGuide && !openDialog) {
      return (
        <Box sx={{ p: 3 }}>
          {renderDashboardHeader()}
          {renderDashboardSkeleton()}
        </Box>
      );
    }
  }

  if (dashboardViewTab === 0 && error) {
    if (!showDashboardTour && !helpCustomizeGuide && !openDialog) {
      return (
        <Box sx={{ p: 3 }}>
          {renderDashboardHeader()}
          <Alert severity="error">{t("dashboard.errorLoading", { error })}</Alert>
        </Box>
      );
    }
  }

  if (dashboardViewTab === 0 && !statistics) {
    if (!showDashboardTour && !helpCustomizeGuide && !openDialog) {
      return (
        <Box sx={{ p: 3 }}>
          {renderDashboardHeader()}
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{ filter: "blur(4px)", pointerEvents: "none", opacity: 0.7 }}
            >
              <Grid container spacing={3}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <StatCard
                      title={`SAMPLE_STAT_${i}`}
                      value={Math.random() * 1000}
                      unit="%"
                      trend={Math.random() * 20 - 10}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                zIndex: 1,
                bgcolor: theme.palette.background.paper,
                p: 4,
                borderRadius: "12px",
                boxShadow: theme.shadows[6],
                maxWidth: "600px",
                width: "90%",
              }}
            >
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                {t("dashboard.emptyTitle")}
              </Typography>
              <Typography
                variant="body1"
                sx={{ mb: 3, color: theme.palette.text.secondary }}
              >
                {t("dashboard.emptyDescription")}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/organizations")}
                sx={{
                  borderRadius: "12px",
                  px: 3,
                  py: 1.5,
                }}
              >
                {t("dashboard.goToOrganizations")}
              </Button>
            </Box>
          </Box>
        </Box>
      );
    }
  }

  if (
    dashboardViewTab === 1 &&
    isLoadingAggregated &&
    !aggregatedStatistics
  ) {
    if (!showDashboardTour && !helpCustomizeGuide && !openDialog) {
      return (
        <Box sx={{ p: 3 }}>
          {renderDashboardHeader()}
          {renderDashboardSkeleton()}
        </Box>
      );
    }
  }

  if (dashboardViewTab === 1 && aggregatedError && !aggregatedStatistics) {
    if (!showDashboardTour && !helpCustomizeGuide && !openDialog) {
      return (
        <Box sx={{ p: 3 }}>
          {renderDashboardHeader()}
          <Alert severity="error">{aggregatedError}</Alert>
        </Box>
      );
    }
  }

  if (!displayStatistics && !showDashboardTour && !helpCustomizeGuide && !openDialog) {
    return (
      <Box sx={{ p: 3 }}>
        {renderDashboardHeader()}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <Typography color="text.secondary">{t("dashboard.noData")}</Typography>
        </Box>
      </Box>
    );
  }

  const stats = (displayStatistics ?? {}) as DashboardStatistics;

  const getStatData = (stat: StatisticType): unknown => {
    const key = stat as keyof DashboardStatistics;
    return stats[key] as unknown;
  };

  const hasDataForStat = (stat: StatisticType): boolean => {
    const data = getStatData(stat);
    if (data == null) return false;
    if (typeof data === "number") return true;
    if (Array.isArray(data)) {
      if (data.length === 0) return false;
      const first = data[0] as Record<string, unknown> | undefined;
      if (first && typeof first === "object") {
        if ("amount" in first && typeof (first as any).amount === "number") {
          const sum = (data as any[]).reduce(
            (acc, item) =>
              acc + (typeof item.amount === "number" ? item.amount : 0),
            0
          );
          return sum !== 0;
        }
        if (
          "supply_amount" in first &&
          typeof (first as any).supply_amount === "number"
        ) {
          const sum = (data as any[]).reduce(
            (acc, item) =>
              acc +
              (typeof item.supply_amount === "number" ? item.supply_amount : 0),
            0
          );
          return sum !== 0;
        }
      }
      return true;
    }
    return true;
  };

  const renderStatCard = (stat: StatisticType) => {
    const key = stat as keyof DashboardStatistics;
    if (stats[key] === undefined || stats[key] === null) return null;

    const data = stats[key] as unknown;
    const chartType = chartTypes[stat] || "bar";

    if (Array.isArray(data)) {
      if (stat === StatisticType.SUPPLY_PER_RECYCLER) {
        return (
          <ChartCard
            title={getDisplayTitle(stat)}
            data={data}
            type={chartType}
            dataKey="supply_amount"
            nameKey="recycler_name"
            onMenuClick={(e) => handleMenuOpen(e, stat)}
          />
        );
      }
      if (stat === StatisticType.INCOMING_WASTE_BREAKDOWN) {
        return (
          <ChartCard
            title={getDisplayTitle(stat)}
            data={data}
            type={chartType}
            dataKey="amount"
            nameKey="date"
            onMenuClick={(e) => handleMenuOpen(e, stat)}
          />
        );
      }
      if (stat === StatisticType.WASTE_TYPE_BREAKDOWN) {
        const transformedData = (data as any[]).map((item) => ({
          waste_type: item.waste_type,
          total_kg: item.total_kg,
        }));
        return (
          <ChartCard
            title={getDisplayTitle(stat)}
            data={transformedData}
            type="pie"
            donut
            dataKey="total_kg"
            nameKey="waste_type"
            onMenuClick={(e) => handleMenuOpen(e, stat)}
            valueMaximumFractionDigits={
              dashboardViewTab === 1 ? 2 : undefined
            }
          />
        );
      }
    }

    if (typeof data === "number") {
      const calculateTrend = (
        current: number,
        previous: number | undefined
      ): number => {
        if (previous === undefined || previous === 0) return 0;
        return ((current - previous) / previous) * 100;
      };

      const currentValue = data;
      const previousKey = `previous_month_${stat}` as keyof DashboardStatistics;
      const previousValue =
        typeof stats[previousKey] === "number"
          ? (stats[previousKey] as number)
          : undefined;
      const change = calculateTrend(currentValue, previousValue);

      return (
        <StatCard
          title={getDisplayTitle(stat)}
          value={currentValue}
          unit={getUnitForStat(stat)}
          trend={change}
          hideTrend={dashboardViewTab === 1}
        />
      );
    }

    return null;
  };

  const isChartStat = (stat: StatisticType) => {
    const data = getStatData(stat);
    return Array.isArray(data);
  };

  const isNumberStat = (stat: StatisticType) => {
    const data = getStatData(stat);
    return typeof data === "number";
  };

  const isMonthlyTab = dashboardViewTab === 0;
  const tabStats = isMonthlyTab ? selectedStats : selectedOverviewStats;
  const tabExtras = isMonthlyTab ? monthlyExtras : overviewExtras;
  const extraIdsForTab = getDashboardExtrasForOrgTab(
    selectedOrganization.organization_type,
    isMonthlyTab ? "monthly" : "overview",
  );
  const statIdsForTab = getStatsForOrgType(
    selectedOrganization.organization_type,
  );
  const extraLabel = (id: DashboardExtraWidgetId) =>
    id === DASHBOARD_EXTRA_MAP
      ? selectedOrganization.organization_type === OrganizationType.TRUCK
        ? t("dashboard.extras.truckMap")
        : t("dashboard.extras.containerMap")
      : t("dashboard.extras.kpiProgress");
  const customizeItems = [
    ...extraIdsForTab.map((id) => ({ id, label: extraLabel(id) })),
    ...statIdsForTab.map((id) => ({ id, label: getDisplayTitle(id) })),
  ];
  const customizeSelected = [...tabExtras, ...tabStats];

  const numericSelectedStats = tabStats.filter(
    (stat) => isNumberStat(stat) && hasDataForStat(stat)
  );

  const chartSelectedStats = tabStats.filter(
    (stat) => isChartStat(stat) && hasDataForStat(stat)
  );

  const kpiLimitedKeys = limits
    .filter((l) => l.isEnabled)
    .map((l) => l.name.toLowerCase());

  // Charts + Map share one section; the map sits in a "spotlight" row beside the first chart.
  const hasCharts = chartSelectedStats.length > 0;
  const mapHeight = hasCharts ? 420 : 500;
  const [firstChart, ...restCharts] = chartSelectedStats;
  const mapContainers = (stats.containers ?? []).filter(
    (c) => c.latitude != null && c.longitude != null
  );
  const mapTrucks = (stats.active_trucks ?? []).filter(
    (t) => t.latitude != null && t.longitude != null
  );

  const mapNode =
    tabExtras.includes(DASHBOARD_EXTRA_MAP) &&
    dashboardViewTab === 0 &&
    isSiteOperationsOrgType(selectedOrganization.organization_type) &&
    mapContainers.length > 0 ? (
      <ContainerMap height={mapHeight} containers={mapContainers} />
    ) : tabExtras.includes(DASHBOARD_EXTRA_MAP) &&
      selectedOrganization.organization_type === OrganizationType.TRUCK &&
      mapTrucks.length > 0 ? (
      <TruckMap height={mapHeight} trucks={mapTrucks} />
    ) : null;

  return (
    <Box sx={{ p: 3 }}>
      {renderDashboardHeader()}

      {/* Key Numbers — quick-scan summary metrics first */}
      {numericSelectedStats.length > 0 && (
        <SetupCoachmark
          open={showDashboardTour && tourPhase === "keyNumbers"}
          title={t("selfSetup.tour.keyNumbersTitle")}
          subtitle={t("selfSetup.tour.keyNumbersSubtitle")}
          onDismiss={softDismissDashboardTour}
          onNext={goNextDashboardTip}
          nextLabel={t("selfSetup.tourNext")}
          fullWidth
        >
          <Box id="self-setup-dashboard-key-numbers" sx={{ mb: 3, width: "100%" }}>
            {renderSectionHeader(t("dashboard.sections.keyNumbers"))}
            <Grid container spacing={2.5} sx={{ alignItems: "stretch" }}>
              {numericSelectedStats.map((stat) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={stat}>
                  {renderStatCard(stat)}
                </Grid>
              ))}
            </Grid>
          </Box>
        </SetupCoachmark>
      )}

      {/* KPI Progress — goal tracking */}
      {stats && hasEnabledKPIs && tabExtras.includes(DASHBOARD_EXTRA_KPI) && (
        <Box sx={{ mb: 3 }}>
          {renderSectionHeader(t("dashboard.sections.kpiProgress"))}
          <Grid container spacing={2.5}>
            {Object.entries(stats)
              .filter(
                ([key, value]) =>
                  typeof value === "number" &&
                  !key.startsWith("previous_month_") &&
                  kpiLimitedKeys.includes(key)
              )
              .sort(
                (a, b) =>
                  Number((a[1] as number) === 0) -
                  Number((b[1] as number) === 0)
              )
              .map(([key, value]) => (
                <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={key}>
                  <KPIProgressCard
                    title={getDisplayTitle(key)}
                    value={value as number}
                    unit={KPI_UNITS[key] ?? ""}
                  />
                </Grid>
              ))}
          </Grid>
        </Box>
      )}

      {/* Charts & Map — trends, breakdowns, and live locations */}
      {(hasCharts || mapNode) && (
        <SetupCoachmark
          open={showDashboardTour && tourPhase === "charts"}
          title={t("selfSetup.tour.chartsTitle")}
          subtitle={t("selfSetup.tour.chartsSubtitle")}
          onDismiss={softDismissDashboardTour}
          onNext={goNextDashboardTip}
          nextLabel={t("selfSetup.tourNext")}
          fullWidth
        >
          <Box id="self-setup-dashboard-charts" sx={{ mb: 3, width: "100%" }}>
            {renderSectionHeader(
              mapNode
                ? t("dashboard.sections.chartsAndMap")
                : t("dashboard.sections.charts"),
            )}

            {/* Spotlight row: map alongside the first chart */}
            {mapNode && (
              <Grid
                container
                spacing={2.5}
                sx={{
                  alignItems: "stretch",
                  mb: restCharts.length > 0 ? 2.5 : 0,
                }}
              >
                <Grid item xs={12} md={hasCharts ? 7 : 12}>
                  {mapNode}
                </Grid>
                {firstChart && (
                  <Grid item xs={12} md={5}>
                    {renderStatCard(firstChart)}
                  </Grid>
                )}
              </Grid>
            )}

            {/* Remaining charts (or all charts when there is no map) */}
            {(mapNode ? restCharts : chartSelectedStats).length > 0 && (
              <Grid container spacing={2.5} sx={{ alignItems: "stretch" }}>
                {(mapNode ? restCharts : chartSelectedStats).map((stat) => (
                  <Grid item xs={12} md={6} key={stat}>
                    {renderStatCard(stat)}
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </SetupCoachmark>
      )}

      {/* Main Statistics Grid - Show selected stats */}
      {/* <Grid container spacing={3}>
        {selectedStats
          .sort(
            (a, b) => Number(!hasDataForStat(a)) - Number(!hasDataForStat(b))
          )
          .map((stat) => (
            <Grid item xs={12} sm={6} md={4} key={stat}>
              {renderStatCard(stat)}
            </Grid>
          ))}
      </Grid> */}

      {/* Context Menus and Dialogs */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleChartTypeChange("bar")}>
          <ListItemIcon>
            <BarChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("dashboard.chartTypes.bar")}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleChartTypeChange("line")}>
          <ListItemIcon>
            <LineChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("dashboard.chartTypes.line")}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleChartTypeChange("area")}>
          <ListItemIcon>
            <AreaChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("dashboard.chartTypes.area")}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleChartTypeChange("pie")}>
          <ListItemIcon>
            <PieChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("dashboard.chartTypes.pie")}</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.28 : 0.12)}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? `0 24px 64px ${alpha("#000", 0.55)}`
                : `0 20px 50px ${alpha("#0f172a", 0.16)}`,
          },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            position: "relative",
            px: { xs: 2.5, sm: 3 },
            pt: 2.75,
            pb: 2.25,
            // DialogTitle inherits Typography-h6; reset so nested title/subtitle layout cleanly.
            fontSize: "inherit",
            fontWeight: "inherit",
            lineHeight: "inherit",
            letterSpacing: "inherit",
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.28)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 55%, transparent 100%)`
                : `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 55%, ${theme.palette.background.paper} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.1)}`,
          }}
        >
          <IconButton
            aria-label={t("common.close")}
            onClick={handleCloseDialog}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              insetInlineEnd: 12,
              color: "text.secondary",
              bgcolor: alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.2 : 0.04),
              "&:hover": {
                bgcolor: alpha(
                  theme.palette.common.black,
                  theme.palette.mode === "dark" ? 0.32 : 0.08,
                ),
              },
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              pr: 5,
            }}
          >
            <PageTitleIcon icon={<SettingsIcon />} size={48} />
            <Box sx={{ minWidth: 0, pt: 0.25 }}>
              <Typography
                component="h2"
                sx={{
                  fontFamily: APP_FONT_FAMILY,
                  fontWeight: 800,
                  fontSize: { xs: "1.2rem", sm: "1.35rem" },
                  letterSpacing: "-0.02em",
                  lineHeight: 1.25,
                  color: "text.primary",
                }}
              >
                {isMonthlyTab
                  ? t("dashboard.customizeMonthly")
                  : t("dashboard.customizeOverview")}
              </Typography>
              <Typography
                component="p"
                sx={{
                  fontFamily: APP_FONT_FAMILY,
                  mt: 0.5,
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  lineHeight: 1.45,
                  color: "text.secondary",
                }}
              >
                {isMonthlyTab
                  ? t("dashboard.customizeMonthlyHint")
                  : t("dashboard.customizeOverviewHint")}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers={false}
          sx={{
            maxHeight: "55vh",
            px: { xs: 2.5, sm: 3 },
            pt: 2.5,
            pb: 1,
          }}
        >
          <SetupCoachmark
            open={
              showCustomizeDialogTip &&
              activeCustomizeDialogPhase === "search"
            }
            title={t("selfSetup.dashboardGuide.step2")}
            subtitle={t("selfSetup.dashboardGuide.intro")}
            onDismiss={softDismissDashboardTour}
            onNext={advanceCustomizeDialogTour}
            nextLabel={t("selfSetup.tourNext")}
            fullWidth
          >
            <Box id="help-customize-search" sx={{ mb: 2, width: "100%" }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t("dashboard.searchMetrics")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, opacity: 0.6 }}>
                      <SearchIcon />
                    </Box>
                  ),
                }}
              />
            </Box>
          </SetupCoachmark>

          <SetupCoachmark
            open={
              showCustomizeDialogTip &&
              activeCustomizeDialogPhase === "metrics"
            }
            title={t("selfSetup.dashboardGuide.step3")}
            subtitle={t("selfSetup.tour.dashboardSubtitle")}
            onDismiss={softDismissDashboardTour}
            onNext={advanceCustomizeDialogTour}
            nextLabel={t("selfSetup.tourNext")}
            fullWidth
          >
            <Box id="help-customize-metrics" sx={{ width: "100%" }}>
              <DashboardCustomizeWidgetList
                items={customizeItems}
                selectedIds={customizeSelected}
                searchQuery={searchQuery}
                selectAllLabel={t("dashboard.selectAllMetrics")}
                onChange={(next) => {
                  const extras = extraIdsForTab.filter((id) => next.includes(id));
                  const stats = statIdsForTab.filter((id) => next.includes(id));
                  if (isMonthlyTab) {
                    setMonthlyExtras(extras);
                    setSelectedStats(stats);
                    sessionStorage.setItem(
                      ZERO_MONTHLY_EXTRAS_KEY,
                      JSON.stringify(extras),
                    );
                  } else {
                    setOverviewExtras(extras);
                    setSelectedOverviewStats(stats);
                    sessionStorage.setItem(
                      ZERO_OVERVIEW_EXTRAS_KEY,
                      JSON.stringify(extras),
                    );
                    sessionStorage.setItem(
                      ZERO_OVERVIEW_STATS_KEY,
                      JSON.stringify(stats),
                    );
                  }
                }}
              />
            </Box>
          </SetupCoachmark>
        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 2,
            gap: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(
              theme.palette.primary.main,
              theme.palette.mode === "dark" ? 0.06 : 0.03,
            ),
          }}
        >
          <Button
            onClick={handleCloseDialog}
            color="inherit"
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
          >
            {t("common.cancel")}
          </Button>
          <SetupCoachmark
            open={
              showCustomizeDialogTip &&
              activeCustomizeDialogPhase === "save"
            }
            title={t("selfSetup.dashboardGuide.step4")}
            subtitle={t("selfSetup.dashboardGuide.dialogTipBody")}
            onDismiss={softDismissDashboardTour}
            onNext={advanceCustomizeDialogTour}
            nextLabel={t("selfSetup.tourDone")}
          >
            <Button
              id="help-customize-save"
              onClick={handleSavePreferences}
              variant="contained"
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 700,
                px: 2.5,
                boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              {t("dashboard.saveChanges")}
            </Button>
          </SetupCoachmark>
        </DialogActions>
      </Dialog>

      <KPILimitsDialog
        open={openKPIDialog}
        onClose={handleCloseKPIDialog}
        helpGuide={helpKpiGuide && kpiTourPhase === "form"}
        onHelpGuideDone={() => {
          setHelpKpiGuide(false);
          setKpiTourPhase("button");
          clearHelpGuideActive();
        }}
        tourTip={
          showDashboardTour &&
          (tourPhase === "kpiForm" || tourPhase === "kpiList")
            ? tourPhase
            : null
        }
        onTourNext={goNextDashboardTip}
        onTourDismiss={softDismissDashboardTour}
        tourFormTitle={t("selfSetup.tour.kpiFormTitle")}
        tourFormSubtitle={t("selfSetup.tour.kpiFormSubtitle")}
        tourListTitle={t("selfSetup.tour.kpiListTitle")}
        tourListSubtitle={t("selfSetup.tour.kpiListSubtitle")}
        tourNextLabel={t("selfSetup.tourNext")}
      />
    </Box>
  );
};

const Dashboard: React.FC = () => {
  const isPilot = useAuthStore((state) => state.tokenPayload?.is_pilot === true);
  return isPilot ? <PilotDashboard /> : <StandardDashboard />;
};

export default Dashboard;
